import React, { useState, useEffect } from 'react';
import styles from './PaymentsModal.module.css';
import { FaPlus, FaMoneyBillWave, FaTimes } from 'react-icons/fa';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebaseConfig';
import AddPaymentModal from './AddPaymentModal';
import { useTheme } from '../../../contexts/ThemeContext';

const PaymentsModal = ({ selectedEmployee, onClose }) => {
    const { isDarkMode } = useTheme();
    const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
    const [payments, setPayments] = useState([]);

    const fetchPayments = async () => {
        if (!selectedEmployee?.id) return;

        try {
            // Get salary payments
            const salaryQuery = query(
                collection(db, 'Salaries'),
                where('employeeId', '==', selectedEmployee.id)
            );
            const salarySnapshot = await getDocs(salaryQuery);
            
            let allPayments = [];
            
            // Process salary payments
            salarySnapshot.docs.forEach(doc => {
                const salaryData = doc.data();
                if (salaryData.payments) {
                    Object.entries(salaryData.payments).forEach(([yearMonth, payment]) => {
                        allPayments.push({
                            id: `${doc.id}-${yearMonth}`,
                            paymentDate: payment.paidDate,
                            paymentType: 'Maaş Ödemesi',
                            amount: payment.amount,
                            description: `${yearMonth} dönemi maaş ödemesi`,
                            status: 'Ödendi'
                        });
                    });
                }
            });

            // Get other payments
            const paymentsQuery = query(
                collection(db, 'Payments'),
                where('employeeId', '==', selectedEmployee.id)
            );
            const paymentsSnapshot = await getDocs(paymentsQuery);

            // Process other payments
            paymentsSnapshot.docs.forEach(doc => {
                const paymentData = doc.data();
                allPayments.push({
                    id: doc.id,
                    paymentDate: paymentData.paymentDate,
                    paymentType: paymentData.paymentType || 'Diğer Ödeme',
                    amount: paymentData.paidAmount,
                    description: paymentData.description,
                    status: paymentData.status
                });
            });

            // Sort payments by date (most recent first)
            allPayments.sort((a, b) => 
                new Date(b.paymentDate) - new Date(a.paymentDate)
            );

            setPayments(allPayments);
        } catch (error) {
            console.error('Ödeme bilgileri alınırken hata:', error);
        }
    };

    useEffect(() => {
        fetchPayments();
    }, [selectedEmployee]);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', { 
            style: 'currency', 
            currency: 'TRY' 
        }).format(amount);
    };

    const getStatusClass = (status) => {
        switch (status.toLowerCase()) {
            case 'ödendi':
            case 'odendi':
                return styles.statusPaid;
            case 'beklemede':
            case 'bekliyor':
                return styles.statusPending;
            case 'iptal edildi':
            case 'iptal':
                return styles.statusCancelled;
            default:
                return styles.statusPaid;
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={handleOverlayClick}>
            <div className={styles.modalContent}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>
                        <FaMoneyBillWave className={styles.modalIcon} />
                        Ödeme Geçmişi
                    </h2>
                    <button
                        onClick={() => setShowAddPaymentModal(true)}
                        className={styles.addButton}
                    >
                        <FaPlus /> Yeni Ödeme Ekle
                    </button>
                    <button 
                        className={styles.closeButton} 
                        onClick={onClose}
                        aria-label="Kapat"
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.tableContainer}>
                        <table className={styles.paymentsTable}>
                            <thead>
                                <tr>
                                    <th>Ödeme Tarihi</th>
                                    <th>Ödeme Türü</th>
                                    <th>Tutar</th>
                                    <th>Açıklama</th>
                                    <th>Durum</th>
                                </tr>
                            </thead>
                            <tbody>
                                {payments && payments.length > 0 ? (
                                    payments.map((payment) => (
                                        <tr key={payment.id}>
                                            <td>{formatDate(payment.paymentDate)}</td>
                                            <td>{payment.paymentType}</td>
                                            <td className={styles.amount}>{formatCurrency(payment.amount)}</td>
                                            <td>{payment.description}</td>
                                            <td>
                                                <span className={`${styles.statusBadge} ${getStatusClass(payment.status)}`}>
                                                    {payment.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className={styles.emptyMessage}>
                                            Bu çalışana ait herhangi bir ödeme kaydı bulunamadı.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className={styles.formActions}>
                    <button 
                        type="button" 
                        className={`${styles.actionButton} ${styles.dangerButton}`} 
                        onClick={onClose}
                    >
                        Kapat
                    </button>
                </div>
            </div>

            {showAddPaymentModal && (
                <AddPaymentModal
                    onClose={() => setShowAddPaymentModal(false)}
                    onPaymentAdded={() => {
                        fetchPayments();
                        setShowAddPaymentModal(false);
                    }}
                    employeeId={selectedEmployee.id}
                />
            )}
        </div>
    );
};

export default PaymentsModal;